// A type-checking key-validating object lookup function

import JsonURL from "@jsonurl/jsonurl";
import React from "react";
import { NavigateOptions } from "react-router";
import { useSearchParams } from "react-router-dom";

// Intended use is safer-parsing of JSON
export function valueOrDefault(_in: any, _key: string, _type: string, _default: any) {
  // First, verify the input is an object
  if (typeof _in !== "object") {
    throw new TypeError("valueOrDefault: _in is not an object");
  }

  // Now try to read this _key from the object
  // If the key doesn't exists, the resulting `val` will be undefined
  let val = _in[_key];
  if (val === undefined || val === null) {
    return _default;
  }

  // Check that the type matches what we want
  if (typeof val !== _type) {
    if (_type === "Date") {
      if (typeof val === "string" || typeof val === "number") {
        return new Date(val);
      }
    } else if (_type === "BigInt") {
      if (typeof val === "string" || typeof val === "number") {
        return BigInt(val);
      }
    }
    throw new TypeError(`valueOrDefault: Type of looked up value (${typeof val}) does not match expected ${_type} for key ${_key}`);
  }

  return val;
}

export function valueOrThrow(_in: any, _key: string, _type: string) {
  const result = valueOrDefault(_in, _key, _type, undefined);
  if (result === undefined) {
    throw new ReferenceError("valueOrThrow: key \"" + _key + "\" not found");
  }
  return result;
}

export function setDocumentTitle(parts?: string | string[]) {
  if (parts) {
    if (Array.isArray(parts)) {
      document.title = parts.join(" ‹ ") + ` ‹ ${import.meta.env.VITE_TITLE}`;
    } else document.title = `${parts} ‹ ${import.meta.env.VITE_TITLE}`
  } else {
    document.title = `${import.meta.env.VITE_BRAND} ${import.meta.env.VITE_TITLE}`;
  }
}

export function idToTitle(id: string): string {
  // Replace all underscores with spaces
  let title: string = id.replaceAll("_", " ").replaceAll("-", " ").trim();

  // titlecase
  title = title.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });

  if (title.startsWith('Api')) {
    title = title.replace('Api', 'API');
  }

  return title;
}

// The API uses "|" to separate nested category levels; display them with "»".
export function formatNestedLabel(label: string | undefined | null): string {
  return (label ?? "").split("|").map((part) => part.trim()).join(" \u00bb ");
}

export function numberFromTextInput(val: string): string {
  // If number, capture the first numeric part of the string, and use only that as the value
  const matches = /-?\d*\.?(\d+)?/.exec(val);
  if (matches) {
    return matches[0];
  } else return "";
}

// signed defaults to true
export function integerFromTextInput(val: string, signed?: boolean): string {
  // If number, capture the first numeric part of the string, and use only that as the value
  const matches = (signed || signed === undefined) ? /-?\d+/.exec(val) : /\d+/.exec(val);
  if (matches) {
    return matches[0];
  } else return "";
}

export function randomString(length: number, chars?: string): string {
  chars = chars || "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function randomPassword(): string {
  const uppers = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowers = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const symbols = "~!@#$%^&*-_=+.?";
  const alphanum = uppers + lowers + digits;

  let result = randomString(1 + Math.floor(Math.random() * 3), uppers) +
    randomString(1 + Math.floor(Math.random() * 3), lowers) +
    randomString(1 + Math.floor(Math.random() * 3), digits) +
    randomString(1 + Math.floor(Math.random() * 3), symbols);

  const totalLength = 10 + Math.random() * 8;
  for (let i = result.length; i < totalLength; i++) {
    result += alphanum.charAt(Math.floor(Math.random() * alphanum.length));
  }

  return result.split('').sort(function () { return 0.5 - Math.random() }).join('');
}

// Converts an amount of data in bytes to a formatted string
// asBinary, defaults to false, will return it as, e.g. KiB instead of KB (1024 bytes vs 1000)
export function bytesToString(data: number, asBinary?: boolean): string {
  const divisor = asBinary ? 1024 : 1000;

  // Fix for undefined, null, empty string, etc. inputs
  data = data || 0;

  let divisions = 0;
  while (data >= divisor) {
    data /= divisor;
    divisions++;
  }

  // No decimals if it is exactly whole, 1 if not
  let output = (Math.floor(data) === data ? data.toString() : data.toFixed(1)) + ' ';

  return output + (asBinary ? ["B", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"] : ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"])[divisions];
}

export function bitsToString(data: number): string {
  return bytesToString(data, false).replace('B', 'b');
}

/**
 * This custom hook is a wrapper around `useSearchParams()` that parses and
 * serializes the search param value using the JsonURL library, which permits any
 * JavaScript value to be safely URL-encoded.
 *
 * Borrowed from the React Router docs, but altered to use JsonURL
 */
export function useQueryParam<T>(key: string, default_value: T | undefined = undefined): [T | undefined, (newQuery: T, options?: NavigateOptions) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = searchParams.get(key);

  let value = React.useMemo(() => paramValue ? JsonURL.parse(paramValue, { AQF: true }) : undefined, [paramValue]);
  if (value === undefined)
    value = default_value;

  const setValue = React.useCallback(
    (newValue: T, options?: NavigateOptions) => {
      let newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(key, JsonURL.stringify(newValue, { AQF: true }) || "");
      setSearchParams(newSearchParams, options);
    },
    [key, searchParams, setSearchParams]
  );

  return [value, setValue];
}